import { Controller } from "@hotwired/stimulus"
import { showToast } from "../utils/show_toast"
import { csrfToken } from "../utils/csrf_token"

// Day header editor: name/date links slide open one field panel at a time.
// Check saves, X cancels, trash clears that field and persists.
export default class extends Controller {
  static targets = [
    "nameLink",
    "nameTitle",
    "dateLink",
    "title",
    "dateDisplay",
    "namePanel",
    "datePanel",
    "nameInput",
    "dateInput"
  ]
  static values = {
    url: String,
    dayOne: { type: Boolean, default: false },
    helpId: String
  }

  connect() {
    this.openField = null
    this.snapshot()
  }

  openName(event) {
    event.preventDefault()
    this.open("name")
  }

  openDate(event) {
    event.preventDefault()
    this.open("date")
  }

  cancel(event) {
    event.preventDefault()
    this.restore()
    this.close()
  }

  // Enter saves, Escape cancels, Delete clears (same as the icon actions).
  // Backspace also clears on date inputs; on the name field it still edits text.
  fieldKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save(event)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel(event)
      return
    }

    if (event.key === "Delete") {
      event.preventDefault()
      this.clear(event)
      return
    }

    if (event.key === "Backspace" && event.target.type === "date") {
      event.preventDefault()
      this.clear(event)
    }
  }

  clear(event) {
    event.preventDefault()
    if (!this.openField) return

    if (this.openField === "name") {
      this.nameInputTarget.value = ""
    } else {
      this.dateInputTarget.value = ""
    }

    this.persist()
  }

  save(event) {
    event.preventDefault()
    this.persist()
  }

  async persist() {
    if (!this.urlValue || !this.openField) return

    const body = new FormData()
    body.append("trip_day[name]", this.nameInputTarget.value)
    body.append("trip_day[date]", this.dateInputTarget.value)

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Accept": "text/plain, */*",
          "X-CSRF-Token": csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        },
        body
      })
      if (!response.ok) throw new Error("save failed")

      this.applyDisplay()
      this.snapshot()
      this.close()
    } catch {
      showToast("Couldn't save day details", "error")
    }
  }

  open(field) {
    if (this.openField === field) {
      this.restore()
      this.close()
      return
    }

    if (this.openField) {
      this.restore()
      const outgoing = this.panelFor(this.openField)
      this.openField = field
      this.collapsePanel(outgoing, {
        onDone: () => {
          this.expandPanel(this.panelFor(field))
          this.focusField(field)
        }
      })
      return
    }

    this.openField = field
    this.expandPanel(this.panelFor(field))
    this.focusField(field)
  }

  close() {
    if (!this.openField) return

    this.collapsePanel(this.panelFor(this.openField))
    this.openField = null
  }

  // Animate 0px↔content px. 0fr↔1fr clips the label; 0fr↔Npx does not interpolate
  // (no slide). Measure at grid-template-rows: auto, then animate in px.
  expandPanel(panel) {
    const inner = panel.querySelector(".trip-day-editor-panel-inner")
    if (!inner) {
      panel.classList.add("is-open")
      return
    }

    panel.classList.add("is-open")
    const height = this.panelContentHeight(panel, inner)

    panel.style.transition = "none"
    panel.style.gridTemplateRows = "0px"
    void panel.offsetHeight
    panel.style.transition = ""
    panel.style.gridTemplateRows = `${height}px`
  }

  collapsePanel(panel, { onDone } = {}) {
    panel.classList.remove("is-open")

    const inner = panel.querySelector(".trip-day-editor-panel-inner")
    if (!inner) {
      panel.style.gridTemplateRows = ""
      onDone?.()
      return
    }

    const height = this.panelContentHeight(panel, inner)
    panel.style.transition = "none"
    panel.style.gridTemplateRows = `${height}px`
    void panel.offsetHeight
    panel.style.transition = ""
    panel.style.gridTemplateRows = "0px"

    let finished = false
    const clearInline = (event) => {
      if (event && event.propertyName !== "grid-template-rows") return
      if (finished) return
      finished = true
      window.clearTimeout(fallback)
      panel.style.gridTemplateRows = ""
      panel.removeEventListener("transitionend", clearInline)
      onDone?.()
    }
    const fallback = window.setTimeout(() => clearInline(null), 300)
    panel.addEventListener("transitionend", clearInline)
  }

  panelContentHeight(panel, inner) {
    const previous = panel.style.gridTemplateRows
    const previousTransition = panel.style.transition
    panel.style.transition = "none"
    panel.style.gridTemplateRows = "auto"
    const height = inner.scrollHeight
    panel.style.gridTemplateRows = previous
    panel.style.transition = previousTransition
    void panel.offsetHeight
    return height
  }

  panelFor(field) {
    return field === "name" ? this.namePanelTarget : this.datePanelTarget
  }

  focusField(field) {
    const input = field === "name" ? this.nameInputTarget : this.dateInputTarget
    requestAnimationFrame(() => input.focus())
  }

  snapshot() {
    this.savedName = this.nameInputTarget.value
    this.savedDate = this.dateInputTarget.value
  }

  restore() {
    this.nameInputTarget.value = this.savedName
    this.dateInputTarget.value = this.savedDate
  }

  applyDisplay() {
    const name = this.nameInputTarget.value.trim()
    const date = this.dateInputTarget.value
    const ordinal = this.titleTarget.dataset.ordinalLabel || ""
    const hasName = name !== ""
    const hasDate = date !== ""

    this.titleTarget.textContent = ordinal
    this.titleTarget.classList.toggle("hidden", hasName)

    this.nameTitleTarget.textContent = name
    this.nameTitleTarget.classList.toggle("hidden", !hasName)
    this.nameLinkTarget.classList.toggle("hidden", hasName)

    if (hasDate) {
      this.dateDisplayTarget.textContent = this.formatDate(date)
      this.dateDisplayTarget.classList.remove("hidden")
    } else {
      this.dateDisplayTarget.textContent = ""
      this.dateDisplayTarget.classList.add("hidden")
    }
    this.dateLinkTarget.classList.toggle("hidden", hasDate)

    this.updateHelp(name || ordinal)
  }

  updateHelp(label) {
    if (!this.dayOneValue || !this.helpIdValue) return

    const help = document.getElementById(this.helpIdValue)
    if (!help || help.dataset.helpMode !== "single") return

    help.textContent =
      `New stops will automatically be added to ${label}. Add more days when you're ready to split the plan.`
  }

  formatDate(isoDate) {
    const [year, month, day] = isoDate.split("-").map(Number)
    if (!year || !month || !day) return isoDate

    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    })
  }
}
